import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ErroCampo,
  HelpTip,
  Input,
  Label,
  Select,
} from '@/components/ui';
import { api } from '@/lib/api';
import {
  ROTULO_DO_MODO,
  type ConfigDoPlantao,
  type ModoDeCobranca,
  type MotivoDeNaoCobrar,
  type RegraDePreco,
} from '@/lib/manutencao/plantao-tipos';
import { centavosDoTexto, fraseDoErro, textoDoCentavos, textoDoPreco } from '@/pages/manutencao/plantao-comum';

/**
 * Configurações › Catálogos de contrato › **Plantão** (FECHAMENTO-DO-PLANTAO, 06/10/2026): a tabela de plantão da
 * Route, (29/10) a tabela de plantão de cliente sem contrato, o valor por hora da corretiva avulsa (#515; vazio = não cadastrado, as avulsas ficam na conferência), o
 * limite de plantões não cobrados no mês e os motivos de não cobrar.
 *
 * 🔴 Só admin edita — o servidor confere (`requireAdmin`) e a casca `/admin` já barra quem não é. `podeEditar`
 * existe para a tela nunca mostrar um botão que o servidor vai recusar.
 * ⚠️ Motivo desativado continua NA LISTA, com a palavra "Desativado" — some só das escolhas da aba Plantão. Apagar
 * que desliga + lista que só mostra ativos = item preso para sempre (régua de 02/10).
 */

type ModoOuNada = ModoDeCobranca | '';

const MODOS: ModoDeCobranca[] = ['por_chamado', 'por_hora', 'primeira_hora_demais'];

function normalizar(cru: unknown): ConfigDoPlantao {
  const r = (cru && typeof cru === 'object' ? cru : {}) as Partial<ConfigDoPlantao>;
  return {
    tabelaRoute: r.tabelaRoute ?? null,
    limite: typeof r.limite === 'number' ? r.limite : 3,
    motivos: Array.isArray(r.motivos) ? r.motivos : [],
    avulsaPorHoraCentavos: typeof r.avulsaPorHoraCentavos === 'number' ? r.avulsaPorHoraCentavos : null,
    tabelaSemContrato: r.tabelaSemContrato ?? null,
  };
}

export function PlantaoConfigPanel({ podeEditar = true }: { podeEditar?: boolean }) {
  const [cfg, setCfg] = useState<ConfigDoPlantao | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .get<unknown>('/api/v1/manutencao/plantao/config')
      .then((r) => {
        if (vivo) setCfg(normalizar(r));
      })
      .catch((e: unknown) => {
        if (vivo) setErro(fraseDoErro(e, 'Não foi possível carregar a configuração do plantão agora.'));
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <Alert variant="danger">{erro}</Alert>;
  if (!cfg) {
    return (
      <Card variant="flat">
        <p className="p-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="config-plantao">
      <TabelaDePlantao
        qual="route"
        inicial={cfg.tabelaRoute}
        podeEditar={podeEditar}
        onSalvo={(tabelaRoute) => setCfg((c) => (c ? { ...c, tabelaRoute } : c))}
      />
      <TabelaDePlantao
        qual="sem-contrato"
        inicial={cfg.tabelaSemContrato ?? null}
        podeEditar={podeEditar}
        onSalvo={(tabelaSemContrato) => setCfg((c) => (c ? { ...c, tabelaSemContrato } : c))}
      />
      <ValorDaAvulsa
        inicial={cfg.avulsaPorHoraCentavos}
        podeEditar={podeEditar}
        onSalvo={(avulsaPorHoraCentavos) => setCfg((c) => (c ? { ...c, avulsaPorHoraCentavos } : c))}
      />
      <LimiteDoMes inicial={cfg.limite} podeEditar={podeEditar} onSalvo={(limite) => setCfg((c) => (c ? { ...c, limite } : c))} />
      <Motivos motivos={cfg.motivos} podeEditar={podeEditar} onMudou={(motivos) => setCfg((c) => (c ? { ...c, motivos } : c))} />
    </div>
  );
}

function Cabecalho({ titulo, ajuda }: { titulo: string; ajuda: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line p-4 dark:border-line-dark">
      <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">{titulo}</p>
      <HelpTip texto={ajuda} />
    </div>
  );
}

function Retorno({ r }: { r: { ok: boolean; texto: string } | null }) {
  if (!r) return null;
  return <Alert variant={r.ok ? 'success' : 'danger'}>{r.texto}</Alert>;
}

// ─── As tabelas de plantão: a da Route e (29/10) a de cliente sem contrato ───────
// Um formulário só para as duas (os mesmos três modos): o que muda é o endereço, o texto e as marcas de teste.

type QualTabela = 'route' | 'sem-contrato';

const TABELAS: Record<QualTabela, { titulo: string; ajuda: string; rota: string; salvo: string; campo: 'tabelaRoute' | 'tabelaSemContrato' }> = {
  route: {
    titulo: 'Tabela de plantão da Route',
    ajuda: 'O preço que a IGM cobra da Route pelos plantões dos clientes dela. Vale para todos os clientes da Route. Sem tabela, os plantões da Route ficam em conferência.',
    rota: '/api/v1/manutencao/plantao/config/route',
    salvo: 'Tabela da Route salva.',
    campo: 'tabelaRoute',
  },
  'sem-contrato': {
    titulo: 'Tabela de plantão sem contrato',
    ajuda: 'O preço do plantão de cliente que não tem contrato ativo. O plantão continua em "Precisa da sua conferência": o botão "Lançar" aplica esta tabela com um clique. Sem tabela, o item só avisa que ela falta. Contrato em implantação não é cobrado.',
    rota: '/api/v1/manutencao/plantao/config/sem-contrato',
    salvo: 'Tabela de plantão sem contrato salva.',
    campo: 'tabelaSemContrato',
  },
};

function TabelaDePlantao({
  qual,
  inicial,
  podeEditar,
  onSalvo,
}: {
  qual: QualTabela;
  inicial: RegraDePreco | null;
  podeEditar: boolean;
  onSalvo: (t: RegraDePreco | null) => void;
}) {
  const t = TABELAS[qual];
  const [modo, setModo] = useState<ModoOuNada>(inicial?.modo ?? '');
  const [valor, setValor] = useState(textoDoCentavos(inicial?.valorCentavos));
  const [primeira, setPrimeira] = useState(textoDoCentavos(inicial?.primeiraHoraCentavos));
  const [demais, setDemais] = useState(textoDoCentavos(inicial?.demaisCentavos));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [retorno, setRetorno] = useState<{ ok: boolean; texto: string } | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    const e: Record<string, string> = {};
    let preco: RegraDePreco | null = null;
    if (modo === 'primeira_hora_demais') {
      const p = centavosDoTexto(primeira);
      const d = centavosDoTexto(demais);
      if (p === null) e.primeira = 'Informe o valor da 1ª hora, por exemplo 250,00.';
      if (d === null) e.demais = 'Informe o valor das demais horas, por exemplo 150,00.';
      preco = { modo, valorCentavos: null, primeiraHoraCentavos: p, demaisCentavos: d };
    } else if (modo) {
      const v = centavosDoTexto(valor);
      if (v === null) e.valor = 'Informe o valor, por exemplo 200,00.';
      preco = { modo, valorCentavos: v, primeiraHoraCentavos: null, demaisCentavos: null };
    }
    setErros(e);
    if (Object.keys(e).length > 0) {
      document.getElementById(`plantao-${qual}-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    setSalvando(true);
    setRetorno(null);
    try {
      const r = await api.put<Partial<Record<'tabelaRoute' | 'tabelaSemContrato', RegraDePreco | null>>>(t.rota, { preco });
      onSalvo(r?.[t.campo] ?? preco);
      setRetorno({ ok: true, texto: t.salvo });
    } catch (err) {
      setRetorno({ ok: false, texto: fraseDoErro(err) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card variant="flat" data-testid={`config-plantao-${qual}`}>
      <Cabecalho titulo={t.titulo} ajuda={t.ajuda} />
      <div className="max-w-md min-w-0 space-y-4 p-4">
        {!podeEditar ? (
          <p className="text-sm" data-testid={`config-plantao-${qual}-leitura`}>
            {inicial ? textoDoPreco(inicial) : 'Não cadastrada.'}
          </p>
        ) : (
          <>
            <div>
              <Label htmlFor={`plantao-${qual}-modo`}>Como cobrar</Label>
              <Select
                id={`plantao-${qual}-modo`}
                value={modo}
                onChange={(e) => setModo(e.target.value as ModoOuNada)}
                data-testid={`config-plantao-${qual}-modo`}
              >
                <option value="">Sem tabela</option>
                {MODOS.map((m) => (
                  <option key={m} value={m}>
                    {ROTULO_DO_MODO[m]}
                  </option>
                ))}
              </Select>
            </div>
            {(modo === 'por_chamado' || modo === 'por_hora') && (
              <div>
                <Label htmlFor={`plantao-${qual}-valor`}>{modo === 'por_hora' ? 'Valor por hora (R$)' : 'Valor por atendimento (R$)'}</Label>
                <Input
                  id={`plantao-${qual}-valor`}
                  inputMode="decimal"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  data-testid={`config-plantao-${qual}-valor`}
                />
                <ErroCampo id={`plantao-${qual}-valor`} mensagem={erros.valor} />
              </div>
            )}
            {modo === 'primeira_hora_demais' && (
              <>
                <div>
                  <Label htmlFor={`plantao-${qual}-primeira`}>1ª hora (R$)</Label>
                  <Input
                    id={`plantao-${qual}-primeira`}
                    inputMode="decimal"
                    value={primeira}
                    onChange={(e) => setPrimeira(e.target.value)}
                    data-testid={`config-plantao-${qual}-primeira`}
                  />
                  <ErroCampo id={`plantao-${qual}-primeira`} mensagem={erros.primeira} />
                </div>
                <div>
                  <Label htmlFor={`plantao-${qual}-demais`}>Demais horas (R$ por hora)</Label>
                  <Input
                    id={`plantao-${qual}-demais`}
                    inputMode="decimal"
                    value={demais}
                    onChange={(e) => setDemais(e.target.value)}
                    data-testid={`config-plantao-${qual}-demais`}
                  />
                  <ErroCampo id={`plantao-${qual}-demais`} mensagem={erros.demais} />
                </div>
              </>
            )}
            <Retorno r={retorno} />
            <div className="flex justify-end">
              <Button loading={salvando} onClick={() => void salvar()} data-testid={`config-plantao-${qual}-salvar`}>
                Salvar
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ─── Corretiva avulsa (#515) ─────────────────────────────────────────────────

const SEM_VALOR_DA_AVULSA = 'Sem valor, as avulsas ficam na conferência.';

function ValorDaAvulsa({
  inicial,
  podeEditar,
  onSalvo,
}: {
  inicial: number | null;
  podeEditar: boolean;
  onSalvo: (c: number | null) => void;
}) {
  const [valor, setValor] = useState(textoDoCentavos(inicial));
  const [erro, setErro] = useState<string | undefined>();
  const [retorno, setRetorno] = useState<{ ok: boolean; texto: string } | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    // Campo vazio = não cadastrado (limpa). Escrito, tem de ser um valor.
    let porHoraCentavos: number | null = null;
    if (valor.trim()) {
      porHoraCentavos = centavosDoTexto(valor);
      if (porHoraCentavos === null) {
        setErro('Informe o valor por hora, por exemplo 180,00. Deixe vazio para não cadastrar.');
        document.getElementById('plantao-avulsa-valor')?.focus();
        return;
      }
    }
    setErro(undefined);
    setSalvando(true);
    setRetorno(null);
    try {
      const r = await api.put<{ avulsaPorHoraCentavos?: number | null }>('/api/v1/manutencao/plantao/config/avulsa', { porHoraCentavos });
      const salvo = r && 'avulsaPorHoraCentavos' in r ? (r.avulsaPorHoraCentavos ?? null) : porHoraCentavos;
      onSalvo(salvo);
      setRetorno({ ok: true, texto: salvo === null ? `Valor da corretiva avulsa apagado. ${SEM_VALOR_DA_AVULSA}` : 'Valor da corretiva avulsa salvo.' });
    } catch (err) {
      setRetorno({ ok: false, texto: fraseDoErro(err) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card variant="flat" data-testid="config-plantao-avulsa">
      <Cabecalho
        titulo="Corretiva avulsa (por hora)"
        ajuda="O preço da corretiva num sistema que nenhum contrato ativo do cliente cobre (ou de cliente sem contrato para o sistema). A primeira hora é cobrada inteira; da segunda em diante, a hora conta a partir de 15 minutos. Cliente com contrato em implantação vai para a conferência."
      />
      <div className="max-w-md min-w-0 space-y-4 p-4">
        {!podeEditar ? (
          <p className="text-sm" data-testid="config-plantao-avulsa-leitura">
            {inicial === null ? `Não cadastrado. ${SEM_VALOR_DA_AVULSA}` : textoDoPreco({ modo: 'por_hora', valorCentavos: inicial, primeiraHoraCentavos: null, demaisCentavos: null })}
          </p>
        ) : (
          <>
            <div>
              <Label htmlFor="plantao-avulsa-valor">Valor por hora (R$)</Label>
              <Input
                id="plantao-avulsa-valor"
                inputMode="decimal"
                placeholder="180,00"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                aria-describedby={erro ? 'plantao-avulsa-valor-erro' : 'plantao-avulsa-valor-dica'}
                data-testid="config-plantao-avulsa-valor"
              />
              <ErroCampo id="plantao-avulsa-valor" mensagem={erro} />
              {!erro && !valor.trim() && (
                <p id="plantao-avulsa-valor-dica" className="mt-1 text-sm text-ink-3 dark:text-ink-dark-3" data-testid="config-plantao-avulsa-sem-valor">
                  {SEM_VALOR_DA_AVULSA}
                </p>
              )}
            </div>
            <Retorno r={retorno} />
            <div className="flex justify-end">
              <Button loading={salvando} onClick={() => void salvar()} data-testid="config-plantao-avulsa-salvar">
                Salvar
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ─── Limite do mês ───────────────────────────────────────────────────────────

function LimiteDoMes({ inicial, podeEditar, onSalvo }: { inicial: number; podeEditar: boolean; onSalvo: (n: number) => void }) {
  const [limite, setLimite] = useState(String(inicial));
  const [erro, setErro] = useState<string | undefined>();
  const [retorno, setRetorno] = useState<{ ok: boolean; texto: string } | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    const n = Number(limite);
    if (!Number.isInteger(n) || n < 1 || n > 99) {
      setErro('Informe um número de 1 a 99.');
      document.getElementById('plantao-limite')?.focus();
      return;
    }
    setErro(undefined);
    setSalvando(true);
    setRetorno(null);
    try {
      const r = await api.put<{ limite?: number }>('/api/v1/manutencao/plantao/config/limite', { limite: n });
      onSalvo(r?.limite ?? n);
      setRetorno({ ok: true, texto: 'Limite salvo.' });
    } catch (err) {
      setRetorno({ ok: false, texto: fraseDoErro(err) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card variant="flat" data-testid="config-plantao-limite">
      <Cabecalho
        titulo="Limite de plantões não cobrados no mês"
        ajuda="Quando um cliente passa deste número de plantões não cobrados no mês, a aba Plantão marca o cliente e os administradores recebem um aviso no sino."
      />
      <div className="max-w-md min-w-0 space-y-4 p-4">
        {!podeEditar ? (
          <p className="text-sm tabular-nums">{inicial}</p>
        ) : (
          <>
            <div>
              <Label htmlFor="plantao-limite">Plantões não cobrados por cliente, no mês</Label>
              <Input
                id="plantao-limite"
                type="number"
                min={1}
                max={99}
                value={limite}
                onChange={(e) => setLimite(e.target.value)}
                data-testid="config-plantao-limite-campo"
              />
              <ErroCampo id="plantao-limite" mensagem={erro} />
            </div>
            <Retorno r={retorno} />
            <div className="flex justify-end">
              <Button loading={salvando} onClick={() => void salvar()} data-testid="config-plantao-limite-salvar">
                Salvar
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ─── Motivos de não cobrar ───────────────────────────────────────────────────

function Motivos({
  motivos,
  podeEditar,
  onMudou,
}: {
  motivos: MotivoDeNaoCobrar[];
  podeEditar: boolean;
  onMudou: (m: MotivoDeNaoCobrar[]) => void;
}) {
  const [novo, setNovo] = useState('');
  const [novoExige, setNovoExige] = useState(false);
  const [erroNovo, setErroNovo] = useState<string | undefined>();
  const [editando, setEditando] = useState<{ id: string; nome: string; erro?: string } | null>(null);
  const [retorno, setRetorno] = useState<{ ok: boolean; texto: string } | null>(null);

  async function chamar(fn: () => Promise<{ motivos?: MotivoDeNaoCobrar[] } | undefined>, ok: string): Promise<boolean> {
    setRetorno(null);
    try {
      const r = await fn();
      if (r?.motivos) onMudou(r.motivos);
      setRetorno({ ok: true, texto: ok });
      return true;
    } catch (err) {
      setRetorno({ ok: false, texto: fraseDoErro(err) });
      return false;
    }
  }

  async function adicionar() {
    const nome = novo.trim();
    if (nome.length < 2) {
      setErroNovo('Escreva o nome do motivo (pelo menos 2 letras).');
      document.getElementById('plantao-motivo-novo')?.focus();
      return;
    }
    setErroNovo(undefined);
    const foi = await chamar(
      () => api.post<{ motivos?: MotivoDeNaoCobrar[] }>('/api/v1/manutencao/plantao/config/motivos', { nome, exigeTexto: novoExige }),
      'Motivo adicionado.',
    );
    if (foi) {
      setNovo('');
      setNovoExige(false);
    }
  }

  async function renomear() {
    if (!editando) return;
    const nome = editando.nome.trim();
    if (nome.length < 2) {
      setEditando({ ...editando, erro: 'Escreva o nome do motivo (pelo menos 2 letras).' });
      return;
    }
    const foi = await chamar(
      () => api.put<{ motivos?: MotivoDeNaoCobrar[] }>(`/api/v1/manutencao/plantao/config/motivos/${encodeURIComponent(editando.id)}`, { nome }),
      'Motivo renomeado.',
    );
    if (foi) setEditando(null);
  }

  return (
    <Card variant="flat" data-testid="config-plantao-motivos">
      <Cabecalho
        titulo="Motivos de não cobrar"
        ajuda="A lista que aparece quando alguém marca um plantão como “Não cobrar”. Motivo que pede texto obriga quem escolhe a explicar. Desativar tira o motivo das escolhas, mas os plantões que já usavam continuam com ele."
      />
      <div className="space-y-3 p-4">
        {motivos.length === 0 && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Nenhum motivo cadastrado.</p>}
        <ul className="space-y-2">
          {motivos.map((m) => (
            <li
              key={m.id}
              className="flex flex-col gap-2 rounded-xl border border-line p-3 dark:border-line-dark md:flex-row md:items-center md:justify-between"
              data-testid="config-plantao-motivo"
            >
              {editando?.id === m.id ? (
                <div className="min-w-0 flex-1">
                  <Label htmlFor={`plantao-motivo-${m.id}`}>Nome do motivo</Label>
                  <Input
                    id={`plantao-motivo-${m.id}`}
                    value={editando.nome}
                    onChange={(e) => setEditando({ id: m.id, nome: e.target.value })}
                    data-testid="config-plantao-motivo-nome"
                  />
                  <ErroCampo id={`plantao-motivo-${m.id}`} mensagem={editando.erro} />
                </div>
              ) : (
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className={m.ativo ? 'font-medium' : 'font-medium text-ink-3 dark:text-ink-dark-3'}>{m.nome}</span>
                  {!!m.exigeTexto && (
                    <Badge variant="neutral" size="sm">
                      pede texto
                    </Badge>
                  )}
                  {!m.ativo && (
                    <Badge variant="warning" size="sm">
                      Desativado
                    </Badge>
                  )}
                </span>
              )}
              {podeEditar && (
                <span className="flex flex-wrap gap-2">
                  {editando?.id === m.id ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditando(null)}>
                        Cancelar
                      </Button>
                      <Button size="sm" onClick={() => void renomear()} data-testid="config-plantao-motivo-salvar">
                        Salvar nome
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditando({ id: m.id, nome: m.nome })}
                        data-testid="config-plantao-motivo-renomear"
                      >
                        Renomear
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          void chamar(
                            () =>
                              api.put<{ motivos?: MotivoDeNaoCobrar[] }>(
                                `/api/v1/manutencao/plantao/config/motivos/${encodeURIComponent(m.id)}`,
                                { ativo: !m.ativo },
                              ),
                            m.ativo ? 'Motivo desativado.' : 'Motivo reativado.',
                          )
                        }
                        data-testid="config-plantao-motivo-ativo"
                      >
                        {m.ativo ? 'Desativar' : 'Reativar'}
                      </Button>
                    </>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>

        {podeEditar && (
          <div className="max-w-md space-y-3 rounded-xl border border-dashed border-line p-3 dark:border-line-dark" data-testid="config-plantao-motivo-novo">
            <div>
              <Label htmlFor="plantao-motivo-novo">Novo motivo</Label>
              <Input
                id="plantao-motivo-novo"
                value={novo}
                onChange={(e) => setNovo(e.target.value)}
                placeholder="Ex.: Retorno de serviço"
                data-testid="config-plantao-motivo-novo-nome"
              />
              <ErroCampo id="plantao-motivo-novo" mensagem={erroNovo} />
            </div>
            <Checkbox
              label="Pede que quem escolher explique o motivo"
              checked={novoExige}
              onChange={() => setNovoExige((v) => !v)}
              data-testid="config-plantao-motivo-novo-exige"
            />
            <div className="flex justify-end">
              <Button onClick={() => void adicionar()} data-testid="config-plantao-motivo-adicionar">
                Adicionar
              </Button>
            </div>
          </div>
        )}
        <Retorno r={retorno} />
      </div>
    </Card>
  );
}
